import { birthdayEnabled } from "@/consts"
import { fetchBirthdayStatus, needsBirthdayPrompt } from "@/domain/birthday/api"
import { atom } from "jotai"
import { atomWithRefresh, unwrap } from "jotai/utils"
import profileAtom from "./profile"

/** `login` follows a login outside an order; `order` is an order attempt ("Finalizar pedido", including the login it required). */
type BirthdayPromptTrigger = "login" | "order"

type BirthdayPromptState = {
  open: boolean
  ticket: number
  orderAttemptAsked: boolean
}

const promptStateAtom = atom<BirthdayPromptState>({ open: false, ticket: 0, orderAttemptAsked: false })

const customerIdAtom = atom((get) => get(profileAtom)?.id ?? null)

/** Whether the birthday sheet is requested, and the ticket that gives each opening a fresh form. */
export const birthdayPromptAtom = atom((get) => {
  const { open, ticket } = get(promptStateAtom)
  return { open, ticket }
})

/** No-op unless `VITE_BIRTHDAY_ENABLED`; opens the sheet when the server says the customer has no birthday and never declined; an order attempt asks at most once until an order is placed. */
export const requestBirthdayPromptAtom = atom(null, async (get, set, trigger: BirthdayPromptTrigger) => {
  if (!birthdayEnabled) return
  const customerId = get(customerIdAtom)
  if (!customerId) return
  if (trigger === "order") {
    if (get(promptStateAtom).orderAttemptAsked) return
    set(promptStateAtom, (state) => ({ ...state, orderAttemptAsked: true }))
  }
  const status = await fetchBirthdayStatus()
  if (!status || !needsBirthdayPrompt(status) || get(customerIdAtom) !== customerId) return
  set(promptStateAtom, (state) => (state.open ? state : { ...state, open: true, ticket: state.ticket + 1 }))
})

/** Closes the sheet; skipping keeps the customer eligible for the next order attempt. */
export const closeBirthdayPromptAtom = atom(null, (_get, set) => {
  set(promptStateAtom, (state) => ({ ...state, open: false }))
})

/** Ends the order attempt once an order is placed, so the next attempt may ask again. */
export const endBirthdayOrderAttemptAtom = atom(null, (_get, set) => {
  set(promptStateAtom, (state) => ({ ...state, orderAttemptAsked: false }))
})

/** The logged customer's birthday status for the profile screen, tagged with its customer; re-read when the account changes or on `set(birthdayStatusAtom)`. */
export const birthdayStatusAtom = atomWithRefresh(async (get) => {
  const customerId = get(customerIdAtom)
  return { customerId, status: customerId ? await fetchBirthdayStatus() : null }
})

const lastBirthdayStatusAtom = unwrap(birthdayStatusAtom, (previous) => previous)

/** Non-suspending view of {@link birthdayStatusAtom}: `undefined` until the current customer's first read lands, the previous value while refreshing. */
export const birthdayStatusViewAtom = atom((get) => {
  const loaded = get(lastBirthdayStatusAtom)
  return loaded && loaded.customerId === get(customerIdAtom) ? loaded.status : undefined
})
